<template>
  <div>
    <v-card elevation="0" outlined class="mb-8">
      <v-card-text class="pb-0">
        <list-filter-advanced-form
          :attribute-list-filtered="attributeListFiltered"
          :filter-term-list="filterTermList"
          :filter-term-active="filterTermActive"
          @add-filter-term="addFilterTerm"
          @clear-filter-term-list="clearFilterTermList"
        ></list-filter-advanced-form>

        <v-row justify="space-between">
          <label class="ml-5 och-label">Current Filters</label>

          <v-spacer></v-spacer>

          <v-btn
            color="primary"
            outlined
            small
            class="mr-3 mb-3"
            data-cy="listFilter-advanced-showFilteredAttributes"
            :disabled="!filterTermList.length"
            @click="updateAttributeListSelected"
          >
            Show filtered {{ entityName }} attributes
          </v-btn>

          <v-dialog v-model="showClearConfirm" max-width="300">
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                outlined
                small
                class="mr-3 mb-3"
                data-cy="listFilter-advanced-clearAllFilters"
                :disabled="!filterTermList.length"
                v-on="on"
              >
                Clear filters
              </v-btn></template
            >

            <v-card light>
              <v-card-title class="justify-center">
                <h1>Clear all filters?</h1>
              </v-card-title>

              <v-divider></v-divider>

              <v-card-actions class="px-5">
                <v-row justify="space-between">
                  <v-btn outlined @click="showClearConfirm = false">
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    data-cy="listFilter-advanced-clearAllFilters-confirm"
                    @click="clearFilterTermList"
                  >
                    Clear all
                  </v-btn>
                </v-row>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-row>

        <v-divider></v-divider>

        <list-filter-advanced-table
          :filter-term-list="filterTermList"
          @remove-filter-term="removeFilterTerm"
          @set-filter-term-active="setFilterTermActive"
        ></list-filter-advanced-table>

        <v-divider class="mb-5"></v-divider>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import { uniqBy } from "lodash";
import ListFilterAdvancedForm from "./ListFilterAdvancedForm";
import ListFilterAdvancedTable from "./ListFilterAdvancedTable";
import listFilterMixin from "./listFilterMixin";

export default {
  components: {
    ListFilterAdvancedForm,
    ListFilterAdvancedTable,
  },
  computed: {
    attributeListFiltered() {
      const { attributeListShowable, filterTermList } = this;

      const activeAttributeNameList = filterTermList.map(
        ({ attribute: { name } }) => name
      );

      // Filter out active attributes from options list
      return attributeListShowable.filter(
        ({ name: attributeName }) =>
          !activeAttributeNameList.includes(attributeName)
      );
    },
    booleanValueSet() {
      if (this.filterInput && !this.filterInput.attribute) {
        return;
      }
      if (
        this.filterInput.attribute.valueYes !== undefined &&
        this.filterInput.attribute.valueNo !== undefined
      ) {
        return [
          { text: "True", value: this.filterInput.attribute.valueYes },
          { text: "False", value: this.filterInput.attribute.valueNo },
        ];
      }
      return [
        { text: "True", value: true },
        { text: "False", value: false },
      ];
    },
  },
  data() {
    return {
      filterTermList: [],
      filterTermActive: {},
      ruleBoolean: [
        value => value !== [] || value !== undefined || "Required true/false.",
      ],
      showClearConfirm: false,
    };
  },
  methods: {
    addFilterTerm(filterTerm) {
      this.filterTermList.push(filterTerm);
      this.filterTermActive = {};
      this.updateEntityListFiltered();
    },
    clearFilterTermList() {
      this.filterTermList = [];
      this.showClearConfirm = false;
    },
    filter(entityListFormatted, filterTermList) {
      // Return a list of entities that have at least one value that matches at least one search term
      return entityListFormatted.filter(entityHasMatch);

      function entityHasMatch(entity) {
        return filterTermList.every(
          ({ attribute, operator, value: filterValue }) => {
            const { name: attributeName } = attribute;
            const { fn } = operator;
            const entityValue = entity[attributeName];

            return fn(entityValue, filterValue);
          }
        );
      }
    },
    removeFilterTerm(attributeNameToRemove) {
      const { filterTermList } = this;

      this.filterTermList = filterTermList.filter(
        ({ attribute: { name: attributeName } }) =>
          attributeName !== attributeNameToRemove
      );
      this.updateEntityListFiltered();
    },
    setFilterTermActive(filterTerm) {
      const {
        attribute: { name: attributeName },
      } = filterTerm;

      this.filterTermActive = filterTerm;
      this.removeFilterTerm(attributeName);
    },
    updateAttributeListSelected() {
      const { attributeListSelected, filterTermList } = this;

      const filterTermAttributeList = filterTermList.map(
        ({ attribute }) => attribute
      );

      let updatedAttributeListSelected = attributeListSelected.concat(
        filterTermAttributeList
      );

      updatedAttributeListSelected = uniqBy(
        updatedAttributeListSelected,
        ({ name }) => name
      );

      this.$emit(
        "update-attribute-list-selected",
        updatedAttributeListSelected
      );
    },
    updateEntityListFiltered() {
      const { entityListFormatted, filterTermList } = this;

      if (!Array.isArray(filterTermList) || !filterTermList.length) {
        this.$emit("update-entity-list-filtered", entityListFormatted);
        return;
      }

      this.$emit(
        "update-entity-list-filtered",
        this.filter(entityListFormatted, filterTermList)
      );
    },
  },
  mixins: [listFilterMixin],
  props: {
    attributeListSelected: {
      required: true,
      type: Array,
    },
    attributeListShowable: {
      required: true,
      type: Array,
    },
    entityListFormatted: {
      required: true,
      type: Array,
    },
    entityName: {
      required: true,
      type: String,
    },
    value: {
      required: false,
    },
  },
};
</script>

<style scoped>
.v-chip {
  border-radius: 16px !important;
}
tr:hover {
  background-color: rgba(0, 0, 0, 0) !important;
}
</style>
